// src/contexts/AuthContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect
} from 'react';

import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  register as apiRegister,
  getAuthToken
} from '../services/api';

const AuthContext = createContext(null);

// Helper functions for user storage
const getStoredUser = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

const setStoredUser = (user) => {
  try {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  } catch (e) {
    console.error('Error storing user:', e);
  }
};

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage if available
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);

  // On mount, check for stored token and validate session
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check if we have a stored token
        const token = await getAuthToken();
        
        if (!token) {
          // No token stored, user is not logged in
          setUser(null);
          setStoredUser(null);
          setLoading(false);
          return;
        }
        
        // Token exists, validate it with the server
        const response = await getCurrentUser();
        if (response.data && response.data.user) {
          console.log('Auth check - user loaded:', response.data.user);
          setUser(response.data.user);
          setStoredUser(response.data.user);
        } else {
          setUser(null);
          setStoredUser(null);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        // Token invalid or session expired
        setUser(null);
        setStoredUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    const response = await apiLogin(username, password);
    if (response.data && response.data.user) {
      console.log('Login - user data:', response.data.user);
      console.log('Login - can_manage_payroll:', response.data.user.can_manage_payroll);
      setUser(response.data.user);
      setStoredUser(response.data.user);
    }
    return response.data;
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      // ignore logout errors
    }
    setUser(null);
    setStoredUser(null);
  };

  const register = async (userData) => {
    const response = await apiRegister(userData);
    return response.data;
  };

  const refreshUser = async () => {
    try {
      const response = await getCurrentUser();
      if (response.data && response.data.user) {
        setUser(response.data.user);
        setStoredUser(response.data.user);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    refreshUser,
    loading,
    isAuthenticated: !!user
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;